import type { Node } from "@xyflow/react";
import {
  RETRO_BOARD_PRESET_ID,
  retroBoardToNotes,
  retroNotesCreatedAt,
} from "../../utils/retroBoardNotes";
import { parseTimer } from "./timer-digital/timerState";
import type { WidgetPreset } from "./types";

/**
 * Old decorative presets redrawn as stickers. Nodes carrying an old id render
 * the new sticker straight away, and an editor's load rewrites them for good
 * (CanvasContext's lazy migration).
 */
export const LEGACY_PRESET_IDS: Readonly<Record<string, string>> = {
  "ship-it": "sticker-ship-it",
  "hot-take": "sticker-hot-take",
  "quick-win": "sticker-quick-win",
  "plot-twist": "sticker-plot-twist",
  "speech-bubble": "sticker-speech-callout",
  "draft-stamp": "sticker-draft-stamp",
  "caution-zone": "sticker-blocked",
};

/**
 * Labels of the decorative presets placed from #1656 until #1790, which
 * stored only `html` and `label`, keyed to their preset ids.
 */
export const LEGACY_PRESET_LABELS: Readonly<Record<string, string>> = {
  "Ship It! 🚀": "ship-it",
  "Hot Take 🔥": "hot-take",
  "Quick Win 🏆": "quick-win",
  "Plot Twist 🌀": "plot-twist",
  "Hmm… 💭": "speech-bubble",
  "DRAFT stamp": "draft-stamp",
  "Caution Zone": "caution-zone",
  "Big Brain Energy": "big-brain",
  "Light Bulb 💡": "lightbulb",
  "Neon Sign ✨": "neon-sign",
  "Earth & Moon 🌍": "earth-moon-orbit",
};

/** Every onboarding tour card that shipped (#1656 to #1790). */
export const TOUR_LABELS: ReadonlySet<string> = new Set([
  "Tour: Welcome 🎨",
  "Tour: Navigate 🧭",
  "Tour: Notes 📝",
  "Tour: Text Labels ✏️",
  "Tour: Your Toolkit 🧰",
  "Tour: Complete 🎉",
]);

/**
 * The node's preset id, recovered from its label for a preset-less placement.
 * The onboarding tour's cards resolve to the retired `onboarding-welcome`.
 */
export function widgetPresetId(data: unknown): string {
  const { presetId, html, label } = (data ?? {}) as Record<string, unknown>;
  if (typeof presetId === "string" && presetId) return presetId;
  if (typeof html !== "string" || typeof label !== "string") return "";
  if (TOUR_LABELS.has(label)) return "onboarding-welcome";
  return lookup(LEGACY_PRESET_LABELS, label) ?? "";
}

/**
 * Widgets that no longer exist, still present in canvases from before their
 * removal: the terminal, the invisible onboarding shell, the retro board, the
 * radio, and the old decorative presets that got no sticker. Only web-ui
 * acts on this list; the backend passes widget nodes through unchanged.
 */
export const REMOVED_WIDGET_PRESET_IDS: ReadonlySet<string> = new Set([
  "terminal",
  "onboarding-welcome",
  RETRO_BOARD_PRESET_ID,
  "music-player",
  "big-brain",
  "lightbulb",
  "neon-sign",
  "earth-moon-orbit",
]);

export function isRemovedWidget(node: Node): boolean {
  return (
    node.type === "widgetNode" &&
    REMOVED_WIDGET_PRESET_IDS.has(widgetPresetId(node.data))
  );
}

export function isRetroBoard(node: Node): boolean {
  return (
    node.type === "widgetNode" &&
    widgetPresetId(node.data) === RETRO_BOARD_PRESET_ID
  );
}

/**
 * The nodes to render: removed widgets dropped, each retro board replaced by
 * the notes the migration will write, so its cards show before any editor
 * opens the canvas. Notes already stored win.
 *
 * `previewRetro: false` is for sessions that can write: their own migration
 * stores the real notes, and a preview note there would take edits that
 * never persist. The board stays hidden until the stored notes arrive.
 */
export function displayNodes(
  nodes: readonly Node[],
  { previewRetro = true }: { previewRetro?: boolean } = {},
): Node[] {
  const ids = new Set(nodes.map((n) => n.id));
  return nodes.flatMap((n) => {
    if (!isRemovedWidget(n)) return [n];
    if (!previewRetro || !isRetroBoard(n)) return [];
    return retroBoardToNotes(n, retroNotesCreatedAt(n)).filter(
      (note) => !ids.has(note.id),
    );
  });
}

/** Own entries only: "constructor" is not an old preset. */
function lookup(map: Readonly<Record<string, string>>, key: string) {
  return Object.hasOwn(map, key) ? map[key] : undefined;
}

export function resolvePresetId(presetId: string): string {
  return lookup(LEGACY_PRESET_IDS, presetId) ?? presetId;
}

/**
 * The node rewritten onto its new sticker: new id and label, stored `html`
 * and `sized` dropped, resized to the sticker's placement size with its
 * top-left kept. A timer from before the stopwatch (no `durationSeconds`)
 * gets the same resize, since its stored box is landscape. Null when there
 * is nothing to upgrade.
 */
export function upgradeLegacyWidget(
  node: Node,
  presets: ReadonlyMap<string, WidgetPreset>,
): Node | null {
  if (node.type !== "widgetNode") return null;
  const presetId = widgetPresetId(node.data);
  const {
    presetId: _presetId,
    html: _html,
    sized: _sized,
    ...rest
  } = node.data as Record<string, unknown>;
  if (presetId === "timer-digital") {
    const timer = presets.get(presetId);
    if (!timer || rest.durationSeconds !== undefined) return null;
    return {
      ...node,
      width: timer.defaultWidth,
      height: timer.defaultHeight,
      data: {
        ...rest,
        presetId,
        label: timer.label,
        durationSeconds: parseTimer(rest).durationSeconds,
      },
    };
  }
  const nextId = lookup(LEGACY_PRESET_IDS, presetId);
  const preset = nextId ? presets.get(nextId) : undefined;
  if (!preset) return null;
  return {
    ...node,
    width: preset.defaultWidth,
    height: preset.defaultHeight,
    data: { ...rest, presetId: preset.id, label: preset.label },
  };
}

export interface LegacyMigration {
  /** Notes for retro boards, minus any already stored. */
  add: Node[];
  /** Old presets rewritten whole, so stale `html` and `sized` go too. */
  replace: Node[];
  remove: Node[];
}

/** What the lazy migration writes, planned from live storage. */
export function planLegacyMigration(
  nodes: readonly Node[],
  presets: ReadonlyMap<string, WidgetPreset>,
): LegacyMigration {
  const ids = new Set(nodes.map((n) => n.id));
  const remove = nodes.filter(isRemovedWidget);
  const add = remove
    .filter(isRetroBoard)
    .flatMap((b) => retroBoardToNotes(b, retroNotesCreatedAt(b)))
    .filter((note) => !ids.has(note.id));
  const replace = nodes.flatMap((n) => {
    const next = upgradeLegacyWidget(n, presets);
    return next ? [next] : [];
  });
  return { add, replace, remove };
}
